import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { PlayArrow as PlayIcon, ArrowBack as BackIcon } from "@mui/icons-material";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { Task, SequenceFlow } from "model/activitiesModel";
import MobileSequenceFlowsInline from "./MobileSequenceFlowsInline";
import { getTranslation } from "common";

interface MobileDetailFooterDockProps {
  task: Task;
  onTakeInCarico: () => void;
  onUndo: () => void;
  onSequenceFlowChoice: (flow: SequenceFlow) => void;
  /** When true, the task is editable (doing && !readonly). */
  isEditable: boolean;
  translations: Record<string, string>;
}

/**
 * Footer dock at the bottom of the mobile task detail drawer.
 * - For a todo task: a single "Prendi in Carico" full-width CTA.
 * - For a doing task: a two-column layout with "Riporta in Da Fare" on the left
 *   and the inline sequence flow list on the right.
 */
const MobileDetailFooterDock: React.FC<MobileDetailFooterDockProps> = ({
  task,
  onTakeInCarico,
  onUndo,
  onSequenceFlowChoice,
  isEditable,
  translations,
}) => {
  const flows = task.sequenceflows ?? [];

  // Todo task: single Prendi in Carico button.
  if (task.state === 0) {
    return (
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <FlowentiButton
          id="mobile-detail-take-in-carico"
          size="small"
          variant="contained"
          fullWidth
          startIcon={<PlayIcon sx={{ fontSize: 16 }} />}
          onClick={onTakeInCarico}
          disabled={!isEditable}
          sx={{
            py: 1,
            fontSize: 12,
            fontWeight: 800,
            borderRadius: 3,
          }}
        >
          {getTranslation(translations, "process.action.next")}
        </FlowentiButton>
      </Box>
    );
  }

  // Doing task: split layout.
  return (
    <Box
      sx={{
        p: 1.5,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
        {/* Undo: vertical card on the left */}
        <Box sx={{ flex: "0 0 auto", minWidth: { sm: 130 } }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: 9,
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mb: 0.5,
            }}
          >
            {getTranslation(translations, "process.action.undo")}
          </Typography>
          <FlowentiButton
            id="mobile-detail-undo"
            size="small"
            variant="outlined"
            fullWidth
            startIcon={<BackIcon sx={{ fontSize: 14 }} />}
            onClick={onUndo}
            disabled={!isEditable}
            sx={{
              py: 0.75,
              fontSize: 10,
              fontWeight: 700,
              borderRadius: 2,
              borderColor: "warning.light",
              color: "warning.dark",
            }}
          >
            {getTranslation(translations, "process.action.undo")}
          </FlowentiButton>
        </Box>

        {/* Sequence flow buttons */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {flows.length > 0 ? (
            <MobileSequenceFlowsInline
              flows={flows}
              onChoose={onSequenceFlowChoice}
              translations={translations}
            />
          ) : (
            <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", textAlign: "center", display: "block", py: 1 }}>
              —
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default MobileDetailFooterDock;